import { STOCK_MAGNIFIER } from '@diamond/shared/decimal-util-web';
import { VoucherRewardType } from '@diamond/shared/types';
import { buyerCaptureException } from '@diamond/shared/utils';

import { ProductWithPromo } from './promos';

type VoucherDiscount = {
  product_item_code: number;
  product_name: string;
  division: string;
  quantity: number;
  original_subtotal: number;
  discount_amount: number | null;
  discount_percent: number | null;
  saved_amount: number;
  final_subtotal: number;
  reward_type: VoucherRewardType;
};

type VoucherFreeGood = {
  product_item_code: number;
  product_name: string;
  division: string;
  quantity: number;
  saved_amount: number;
};

export type ProcessedVoucherData = {
  hasVoucherDiscount: boolean;
  hasVoucherFreeGoods: boolean;
  voucherDiscount: VoucherDiscount | null;
  voucherFreeGoods: VoucherFreeGood[];
  /** Discount percent (0-100) or null if not applicable */
  discountPercent: number | null;
  /** Discount amount or null if not applicable */
  discountAmount: number | null;
  /** Unit price after voucher discount (final_subtotal / quantity) */
  finalUnitPrice: number | null;
  /** Unit price before voucher discount (original_subtotal / quantity) */
  originalUnitPrice: number | null;
};

const toArray = <T>(value: T[] | undefined | null): T[] =>
  Array.isArray(value) ? value : [];

/**
 * Calculates unit price from subtotal and quantity
 *
 * Divides subtotal by actual quantity (accounting for stock magnification).
 * Logs to Sentry if quantity is invalid (≤ 0) to help trace data quality issues.
 *
 * @param subtotal - The subtotal amount for the quantity
 * @param quantity - The quantity (magnified, e.g., 5000 for 5 units)
 * @param stockMagnifier - Magnification factor (default: STOCK_MAGNIFIER = 1000)
 * @returns Unit price, or 0 if quantity is invalid
 *
 * @example
 * calculateUnitPrice(500000, 5000); // Returns 100 (500000 / 5)
 * calculateUnitPrice(100000, 0);    // Returns 0, logs to Sentry
 */
const calculateUnitPrice = (
  subtotal: number,
  quantity: number,
  stockMagnifier = STOCK_MAGNIFIER
): number => {
  const actualQuantity = quantity / stockMagnifier;

  if (actualQuantity <= 0) {
    buyerCaptureException(
      {
        message: 'Invalid quantity in unit price calculation',
        quantity,
        subtotal,
      },
      (scope) => {
        scope.setTag('voucher-processing', 'invalid-quantity');
        scope.setContext('calculation-data', {
          quantity,
          subtotal,
          stockMagnifier,
        });
        scope.setLevel('warning');
      }
    );
    return 0;
  }

  return subtotal / actualQuantity;
};

/**
 * Processes voucher-related data for a product
 *
 * Extracts and processes voucher discounts and free goods from product data.
 * Calculates unit prices by dividing voucher subtotals by quantities.
 * Returns discount percentages and amounts directly from API without basis point conversion.
 *
 * @param product - Product with potential voucher discounts and free goods
 * @returns Processed voucher data including:
 *   - hasVoucherDiscount: Whether product has voucher discount applied
 *   - hasVoucherFreeGoods: Whether product has free goods from voucher
 *   - voucherDiscount: First voucher discount object or null
 *   - voucherFreeGoods: Array of free goods from voucher
 *   - discountPercent: Discount percentage (0-100) or null if not applicable
 *   - discountAmount: Fixed discount amount or null if not applicable
 *   - finalUnitPrice: Unit price after voucher discount (final_subtotal / quantity)
 *   - originalUnitPrice: Unit price before voucher discount (original_subtotal / quantity)
 *
 * @example
 * const voucherData = processVoucherData(product);
 * if (voucherData.hasVoucherDiscount && voucherData.discountPercent !== null) {
 *   console.log(`Discount: ${voucherData.discountPercent}%`);
 *   console.log(`Unit price: ${voucherData.finalUnitPrice}`);
 * }
 */
export function processVoucherData(
  product: ProductWithPromo
): ProcessedVoucherData {
  const voucherDiscounts = toArray(
    product.voucher_discounts as VoucherDiscount[] | undefined
  );
  const voucherFreeGoods = toArray(
    product.voucher_free_goods as VoucherFreeGood[] | undefined
  );

  const hasVoucherDiscount = voucherDiscounts.length > 0;
  const hasVoucherFreeGoods = voucherFreeGoods.length > 0;
  const voucherDiscount = hasVoucherDiscount ? voucherDiscounts[0] : null;

  let discountPercent: number | null = null;
  let discountAmount: number | null = null;
  let finalUnitPrice: number | null = null;
  let originalUnitPrice: number | null = null;

  if (voucherDiscount) {
    discountPercent = voucherDiscount.discount_percent;
    discountAmount = voucherDiscount.discount_amount;

    const voucherQuantity = voucherDiscount.quantity || 0;
    if (voucherQuantity > 0) {
      finalUnitPrice = calculateUnitPrice(
        voucherDiscount.final_subtotal,
        voucherQuantity
      );
      originalUnitPrice = calculateUnitPrice(
        voucherDiscount.original_subtotal,
        voucherQuantity
      );
    }
  }

  return {
    hasVoucherDiscount,
    hasVoucherFreeGoods,
    voucherDiscount,
    voucherFreeGoods,
    discountPercent,
    discountAmount,
    finalUnitPrice,
    originalUnitPrice,
  };
}
